import React from "react";
import PageHeader from "../../Components/PageHeader/PageHeader";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Privacy Policy"
        subtitle="Your privacy is important to us. This policy explains how we collect, use, and protect your information."
      />

      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          <p className="text-gray-600 mb-6">
            <strong>Last Updated:</strong> January 12, 2026
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-600 mb-4">
              Welcome to Dijla. We are committed to protecting your personal
              information and your right to privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your
              information when you use our mobile application and website
              (collectively, the "Services").
            </p>
            <p className="text-gray-600">
              Please read this privacy policy carefully. If you do not agree
              with the terms of this privacy policy, please do not access the
              Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Information We Collect
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              Personal Information
            </h3>
            <p className="text-gray-600 mb-4">
              We may collect personal information that you voluntarily provide
              to us when you register for an account, use our Services, or
              contact us. This information may include:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials (username, password)</li>
              <li>Business information (company name, address)</li>
              <li>Shipment and tracking information</li>
              <li>Payment and billing information</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              Automatically Collected Information
            </h3>
            <p className="text-gray-600 mb-4">
              When you access our Services, we may automatically collect certain
              information, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Device information (device type, operating system, unique device identifiers)</li>
              <li>Log information (access times, pages viewed, IP address)</li>
              <li>Location information (with your consent)</li>
              <li>Usage data and analytics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-gray-600 mb-4">
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Providing, operating, and maintaining our Services</li>
              <li>Processing and managing your shipments and tracking requests</li>
              <li>Creating and managing your account</li>
              <li>Communicating with you about orders, updates, and promotional offers</li>
              <li>Improving our Services and developing new features</li>
              <li>Analyzing usage patterns and trends</li>
              <li>Detecting, preventing, and addressing technical issues and fraud</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Sharing Your Information
            </h2>
            <p className="text-gray-600 mb-4">
              We may share your information in the following situations:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>
                <strong>Service Providers:</strong> With third-party vendors and
                service providers who perform services on our behalf (payment
                processing, shipping, analytics)
              </li>
              <li>
                <strong>Business Partners:</strong> With our business partners to
                facilitate shipment and logistics services
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to
                respond to legal process
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger,
                acquisition, or sale of assets
              </li>
              <li>
                <strong>With Your Consent:</strong> When you have given us explicit
                consent to share your information
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Data Security
            </h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction. These measures
              include:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and employee training</li>
            </ul>
            <p className="text-gray-600 mt-4">
              However, no method of transmission over the Internet or electronic
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. Data Retention
            </h2>
            <p className="text-gray-600">
              We retain your personal information for as long as necessary to
              fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law. When we
              no longer need your information, we will securely delete or
              anonymize it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. Your Rights and Choices
            </h2>
            <p className="text-gray-600 mb-4">
              Depending on your location, you may have certain rights regarding
              your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>
                <strong>Access:</strong> Request access to your personal information
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate or
                incomplete information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal
                information
              </li>
              <li>
                <strong>Portability:</strong> Request a copy of your data in a
                portable format
              </li>
              <li>
                <strong>Opt-out:</strong> Opt-out of marketing communications at any
                time
              </li>
            </ul>
            <p className="text-gray-600 mt-4">
              To exercise these rights, please contact us using the information
              provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. Third-Party Services
            </h2>
            <p className="text-gray-600">
              Our Services may contain links to third-party websites or services
              that are not owned or controlled by us. We are not responsible for
              the privacy practices of these third parties. We encourage you to
              review their privacy policies before providing any personal
              information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Children's Privacy
            </h2>
            <p className="text-gray-600">
              Our Services are not intended for children under the age of 13. We
              do not knowingly collect personal information from children under
              13. If we become aware that we have collected personal information
              from a child under 13, we will take steps to delete such
              information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              10. Changes to This Privacy Policy
            </h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last Updated" date. You are advised to
              review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-600">
                <strong>Email:</strong> support@dijla.com
              </p>
              <p className="text-gray-600">
                <strong>Phone:</strong> +20 120 000 0000
              </p>
              <p className="text-gray-600">
                <strong>Address:</strong> Cairo, Egypt
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
